import { API_URL } from "../config";
import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, KeyRound } from 'lucide-react';
import { motion } from 'framer-motion';

import PricingSection from '../components/PricingSection';
import FeaturesSection from '../components/FeaturesSection';
import HowItWorksSection from '../components/HowItWorksSection';
import Footer from '../components/Footer';

const LandingPage = () => {
    const navigate = useNavigate();
    const { login } = useAuth();

    const bottomDustParticles = useMemo(() => {
        return Array.from({ length: 40 }).map((_, i) => ({
            id: i,
            x: Math.random() * 100,
            y: Math.random() * 100,
            size: Math.random() * 3 + 1 + 'px',
            duration: Math.random() * 20 + 15,
            delay: Math.random() * 10,
        }));
    }, []);

    const dustParticles = useMemo(() => {
        return Array.from({ length: 40 }).map((_, i) => ({
            id: i,
            x: Math.random() * 100, 
            y: Math.random() * 60,  
            size: Math.random() * 3 + 1, 
            duration: Math.random() * 10 + 10, 
            delay: Math.random() * 5, 
        }));
    }, []);

    return (
        <div className="min-h-screen bg-[#050505] text-white font-sans overflow-hidden relative selection:bg-purple-500/30">
            {}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[100vw] h-[200vh] pointer-events-none z-0 overflow-hidden" 
                 style={{ 
                    WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 20%, transparent 70%)', 
                    maskImage: 'linear-gradient(to bottom, black 0%, black 20%, transparent 70%)' 
                 }}>
                 
                {}
                <div 
                    className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[120%] md:w-[100%] h-[80%] mix-blend-screen"
                    style={{
                        background: 'radial-gradient(ellipse at 50% 0%, rgba(200, 180, 255, 0.15) 0%, rgba(168, 85, 247, 0.05) 40%, transparent 70%)',
                        filter: 'blur(100px)',
                    }}
                />

                {}
                <motion.div 
                    className="absolute top-[-5%] left-1/2 -translate-x-1/2 w-[95%] md:w-[80%] h-[120%] mix-blend-screen"
                    animate={{ opacity: [0.85, 0.95, 0.85] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                        background: 'conic-gradient(from 180deg at 50% -10%, transparent 120deg, rgba(255,255,255,0.05) 130deg, rgba(255,255,255,0.15) 160deg, rgba(255,255,255,0.3) 180deg, rgba(255,255,255,0.15) 200deg, rgba(255,255,255,0.05) 230deg, transparent 240deg)',
                        filter: 'blur(80px)',
                        transform: 'scaleY(1.8)',
                        transformOrigin: 'top center'
                    }}
                />
                
                {}
                <motion.div 
                    className="absolute top-[-5%] left-1/2 -translate-x-1/2 w-[70%] md:w-[50%] h-[120%] mix-blend-screen"
                    animate={{ opacity: [0.6, 0.8, 0.6], scaleX: [1, 1.02, 1] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                    style={{
                        background: 'conic-gradient(from 180deg at 50% -10%, transparent 140deg, rgba(255,255,255,0.1) 155deg, rgba(255,255,255,0.3) 170deg, transparent 175deg, rgba(255,255,255,0.4) 180deg, transparent 185deg, rgba(255,255,255,0.2) 195deg, rgba(255,255,255,0.1) 210deg, transparent 220deg)',
                        filter: 'blur(40px)',
                        transform: 'scaleY(2)',
                        transformOrigin: 'top center'
                    }}
                />

                {}
                <motion.div 
                    className="absolute top-[-2%] left-1/2 -translate-x-1/2 w-[40%] md:w-[25%] h-[120%] mix-blend-screen"
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                    style={{
                        background: 'conic-gradient(from 180deg at 50% -5%, transparent 165deg, rgba(255,255,255,0.4) 175deg, rgba(255,255,255,0.8) 180deg, rgba(255,255,255,0.4) 185deg, transparent 195deg)',
                        filter: 'blur(20px)',
                        transform: 'scaleY(2.5)',
                        transformOrigin: 'top center'
                    }}
                />

                {}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[80vh] overflow-hidden mix-blend-screen">
                    {dustParticles.map((particle) => (
                        <motion.div
                            key={particle.id}
                            className="absolute rounded-full bg-white shadow-[0_0_8px_2px_rgba(255,255,255,0.6)]"
                            style={{
                                width: particle.size,
                                height: particle.size,
                                left: `${particle.x}%`,
                                top: `${particle.y}%`,
                            }}
                            animate={{
                                y: [0, -100, -200], 
                                x: [0, Math.random() * 40 - 20, Math.random() * 60 - 30], 
                                opacity: [0, 0.8, 0], 
                            }}
                            transition={{
                                duration: particle.duration,
                                repeat: Infinity,
                                ease: "linear",
                                delay: particle.delay,
                            }}
                        />
                    ))}
                </div>
            </div>

            {}
            <div className="relative z-50 w-full px-8 py-4 flex items-center justify-between font-mono bg-transparent sticky top-0">
                <div className="flex items-center gap-8 cursor-pointer">
                    <div className="flex items-center gap-2 text-white">
                        <KeyRound className="w-6 h-6 -rotate-45 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
                        <span className="text-xl font-bold tracking-widest text-white">LINKEY</span>
                    </div>
                    
                    <div className="hidden md:flex items-center gap-6 text-[13px]">
                        <button 
                            onClick={() => {
                                const pricingSection = document.getElementById('pricing');
                                if (pricingSection) {
                                    pricingSection.scrollIntoView({ behavior: 'smooth' });
                                }
                            }}
                            className="font-medium tracking-wide text-gray-400 hover:text-white transition-colors uppercase"
                        >
                            PRICING
                        </button>
                        <button onClick={() => navigate('/login')} className="font-medium tracking-wide text-gray-400 hover:text-white transition-colors uppercase">
                            API
                        </button>
                        <button onClick={() => navigate('/login')} className="font-medium tracking-wide text-gray-400 hover:text-white transition-colors uppercase">
                            DOCS
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate('/login')}
                        className="hidden sm:block px-4 py-2 text-[13px] font-medium uppercase tracking-wide text-gray-400 hover:text-white transition-colors"
                    >
                        LOG IN
                    </button>
                    <button 
                        onClick={() => navigate('/login')}
                        className="px-6 py-2 text-[13px] font-medium uppercase tracking-wide border border-white/20 text-white rounded-full hover:bg-white/10 transition-all"
                    >
                        SIGN UP
                    </button>
                </div>
            </div>

            {}
            <main className="relative z-10 flex flex-col items-center justify-center text-center px-4 pt-24 pb-20 max-w-5xl mx-auto">
                {}
                <div 
                    onClick={() => {
                        const section = document.getElementById('how-it-works');
                        if (section) {
                            section.scrollIntoView({ behavior: 'smooth' });
                        }
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-purple-500/30 bg-purple-500/10 backdrop-blur-sm text-sm text-purple-200 mb-8 cursor-pointer hover:bg-purple-500/20 transition-colors shadow-[0_0_15px_rgba(168,85,247,0.15)]"
                >
                    <span>How it works?</span>
                    <ArrowRight size={14} className="text-purple-300" />
                </div>

                {}
                <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-white to-gray-400 mb-6 leading-[1.1]">
                    Turn your Content<br className="hidden md:block" /> into a Growth Tool
                </h1>

                {}
                <p className="text-lg md:text-xl text-gray-400 max-w-2xl mb-10 leading-relaxed">
                    Control access to your content. Force subscriptions, likes, or actions before revealing destination URLs. Watch your audience expand seamlessly.
                </p>

                {}
                <div className="flex flex-col sm:flex-row items-center gap-6 mt-4">
                    <button 
                        onClick={() => navigate('/login')}
                        className="flex items-center gap-2 px-8 py-3.5 bg-white hover:bg-gray-100 text-black rounded-lg font-bold transition-colors w-full sm:w-auto justify-center shadow-[0_0_20px_rgba(255,255,255,0.15)] text-lg"
                    >
                        Create my first link
                        <ArrowRight size={18} className="text-black" />
                    </button>
                    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                        <span>Free forever</span>
                        <span className="w-1 h-1 rounded-full bg-gray-600"></span>
                        <span>No card required</span>
                    </div>
                </div>
            </main>

            <FeaturesSection />

            <PricingSection />

            <HowItWorksSection />
            
            {}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-[80vh] pointer-events-none z-0 overflow-hidden flex flex-col justify-end">
                {}
                <div 
                    className="absolute bottom-[-5%] left-1/2 -translate-x-1/2 w-[100%] md:w-[60%] h-[60%] mix-blend-screen"
                    style={{
                        background: 'radial-gradient(ellipse at 50% 100%, rgba(200, 180, 255, 0.05) 0%, rgba(168, 85, 247, 0.02) 50%, transparent 70%)',
                        filter: 'blur(60px)',
                    }}
                />

                {}
                <motion.div 
                    className="absolute bottom-[-2%] left-1/2 -translate-x-1/2 w-[80%] md:w-[50%] h-[100%] mix-blend-screen"
                    animate={{ opacity: [0.2, 0.3, 0.2] }}
                    transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                        background: 'conic-gradient(from 0deg at 50% 110%, transparent 140deg, rgba(255,255,255,0.01) 150deg, rgba(255,255,255,0.04) 170deg, rgba(255,255,255,0.08) 180deg, rgba(255,255,255,0.04) 190deg, rgba(255,255,255,0.01) 210deg, transparent 220deg)',
                        filter: 'blur(60px)',
                        transform: 'scaleY(1.5)',
                        transformOrigin: 'bottom center'
                    }}
                />
                
                <motion.div 
                    className="absolute bottom-[-2%] left-1/2 -translate-x-1/2 w-[50%] md:w-[30%] h-[100%] mix-blend-screen"
                    animate={{ opacity: [0.15, 0.25, 0.15], scaleX: [1, 1.05, 1] }}
                    transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                    style={{
                        background: 'conic-gradient(from 0deg at 50% 105%, transparent 150deg, rgba(255,255,255,0.02) 165deg, rgba(255,255,255,0.06) 175deg, rgba(255,255,255,0.1) 180deg, rgba(255,255,255,0.06) 185deg, rgba(255,255,255,0.02) 195deg, transparent 210deg)',
                        filter: 'blur(30px)',
                        transform: 'scaleY(2)',
                        transformOrigin: 'bottom center'
                    }}
                />

                {}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-[80vh] overflow-hidden mix-blend-screen">
                    {bottomDustParticles.map((particle) => (
                        <motion.div
                            key={`bottom-dust-${particle.id}`}
                            className="absolute rounded-full bg-violet-200 shadow-[0_0_8px_2px_rgba(168,85,247,0.5)]"
                            style={{
                                width: particle.size,
                                height: particle.size,
                                left: `${particle.x}%`,
                                bottom: `${particle.y}%`, 
                            }}
                            animate={{
                                y: [0, -100, -300], 
                                x: [0, Math.random() * 60 - 30, Math.random() * 100 - 50], 
                                opacity: [0, 0.7, 0], 
                            }}
                            transition={{
                                duration: particle.duration,
                                repeat: Infinity,
                                ease: "linear",
                                delay: particle.delay,
                            }}
                        />
                    ))}
                </div>
            </div>
            
            <Footer />
        </div>
    );
};

export default LandingPage;
